import {openConfirmDialog, showNotification} from 'platform/components';
import {Box, Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {
  TotalPriceWithAndWithoutVat,
  useCancelSupplierOrderItemsMutation,
  useDeleteSupplierOrderItemsMutation,
} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  useTenant,
} from '@dms/shared';
import {OverviewTotalPrice, useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {SupplierOrderItemEdit, SupplierOrderItemEditProps} from './SupplierOrderItemEdit';

interface ListOfItemsTabProps extends TestIdProps {
  isReadOnly: boolean;
  totalPrice: TotalPriceWithAndWithoutVat | Nullish;
  isPending: boolean;
}

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const {supplierOrderId} = useWarehouseParams();
  const formatCurrency = useFormatCurrency();

  const dispatch = useDispatch();

  const tenant = useTenant();

  const [editDialogProps, setEditDialogProps] = useState<SupplierOrderItemEditProps | null>(null);

  const [deleteSupplierOrderItems] = useDeleteSupplierOrderItemsMutation();
  const [cancelSupplierOrderItems] = useCancelSupplierOrderItemsMutation();

  const totalPriceWithoutVat = formatCurrency(
    props.totalPrice?.totalWithoutVat?.amount ?? 0,
    props.totalPrice?.totalWithoutVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const totalPriceWithVat = formatCurrency(
    props.totalPrice?.totalWithVat?.amount ?? 0,
    props.totalPrice?.totalWithVat?.currency ?? tenant.tenantCurrency,
    2
  );

  const actionCallback: ActionCallback = async ({actionKey, rowId, queryId, refreshData}) => {
    await match(actionKey)
      .with('redirectDetail', 'edit', () => {
        const supplierOrderItemId = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(supplierOrderItemId)) {
          return logError('Item id is not defined!');
        }

        const handleClose = () => {
          setEditDialogProps(null);
          refreshData();
          // Invalidating manually onClose to keep dialog rowControl saving feature intact.
          dispatch(metadaApi.util.invalidateTags(['supplierOrders']));
        };

        setEditDialogProps({
          supplierOrderId,
          supplierOrderItemId,
          dataQueryId: queryId,
          isReadOnly: props.isReadOnly,
          onClose: handleClose,
          'data-testid': testIds.warehouse.supplierOrderDetailOverview('editItem'),
        });
      })
      .with('delete', async () => {
        const itemId = isArray(rowId) ? rowId : [rowId];

        await deleteSupplierOrderItems({orderId: supplierOrderId, body: {itemId}})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('cancel', () => {
        const itemId = isArray(rowId) ? rowId : [rowId];

        openConfirmDialog({
          text: i18n.t('entity.warehouse.actions.cancelSupplierOrderItemsConfirm'),
          onConfirm: () =>
            cancelSupplierOrderItems({orderId: supplierOrderId, body: {itemId}})
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('general.notifications.successfullyCancelled'));
                refreshData();
              })
              .catch(handleApiError),
          'data-testid': testIds.warehouse.supplierOrderDetailOverview('cancelItemsDialog'),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {orderId: supplierOrderId}]),
    [supplierOrderId]
  );

  return (
    <>
      <Show when={editDialogProps}>
        {(editDialogProps) => <SupplierOrderItemEdit {...editDialogProps} />}
      </Show>
      <Box height="95%">
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.warehouseSupplierOrderItems}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          data-testid={WAREHOUSE_GRID_CODES.warehouseSupplierOrderItems}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </>
  );
}
